import { Vector } from '@ardoq/graph';
import { PriorityQueue } from './priorityqueue';
import { vectorDot } from './geometry';

export type Cell = {
  col: number;
  row: number;
};

const cellHash = (cell: Cell) => 1000000 * cell.col + cell.row;

/* information about an explored cell */
type CellInfo = {
  cameFrom: Cell | null;
  gScore: number;
};

/* a map keyed by cell value with specified missing value */
function cellMap<Type>(missing: Type) {
  const map = new Map<number, Type>();

  return {
    empty: () => map.size === 0,
    get: (cell: Cell) => map.get(cellHash(cell)) ?? missing,
    set: (cell: Cell, value: Type) => map.set(cellHash(cell), value),
    has: (cell: Cell) => map.has(cellHash(cell)),
    delete: (cell: Cell) => map.delete(cellHash(cell)),
    values: () => map.values(),
  };
}

/* possible moves (could conveivably, but not usefully, be a parameter) */
const directions: Vector[] = [
  [-1, 0],
  [0, -1],
  [1, 0],
  [0, 1],
];

export function* astar(
  seeds: Cell[],
  isValid: (cell: Cell) => boolean,
  h: (cell: Cell) => number
) {
  const open = new PriorityQueue<Cell>(cellHash);
  const info = cellMap<CellInfo>({
    cameFrom: null,
    gScore: +Infinity,
  });

  /* initialise the solution seeds */

  for (const seed of seeds) {
    info.set(seed, { cameFrom: null, gScore: 0 });
    open.add(seed, h(seed));
  }

  /* keep looking for solution until we run out of options */

  let solution: Cell | null = null;

  while (open.size !== 0 && !solution) {
    const current = open.pop()!.value; // pop can't return undefined but linter doesn't know that

    if (h(current) === 0) {
      /* suggestion scored zero; that makes it a solution */

      solution = current;
    } else {
      /* worse than perfect not a solution; keep looking */

      const cameFrom = info.get(current).cameFrom;
      const currentDirection: Vector = cameFrom
        ? [current.col - cameFrom.col, current.row - cameFrom.row]
        : [0, 0];

      for (const direction of directions) {
        const neighbor = {
          col: current.col + direction[0],
          row: current.row + direction[1],
        };

        /*
          The dot product of two vectors is the cosine of the angle between them multiplied by the product of the
          magnitude of those vectors. Since both currentDirection and direction are, by construction, unit vectors
          we can use the dot product directly as a measure of the corner.

          -1 indicates a U-turn, +1 indicates straight ahead and anything in-between some sort of corner. I'm just
          going to go ahead and quantise those as

            -Inf <  d < -0.5 basically a U-turn
            -0.5 <= d < +0.5 basically a corner
            +0.5 <= d < +Inf basically straight ahead

          If the current direction cannot be determined, [ 0, 0 ] is assumed which is handled as a corner

        */

        const dot = vectorDot(currentDirection, direction);

        if (dot < -0.5 || !isValid(neighbor)) {
          continue;
        }

        const costOfMoveToNeighbour = dot > 0.5 ? 1 : 2;
        const gScore = info.get(current).gScore + costOfMoveToNeighbour;

        if (gScore < info.get(neighbor).gScore) {
          const fScore = gScore + h(neighbor);

          info.set(neighbor, {
            cameFrom: current,
            gScore: gScore,
          });

          open.set(neighbor, fScore); // set or update the priority
        }
      }
    }
  }

  /* dump the solution */

  if (solution) {
    let current: Cell = solution;

    yield current;

    while (info.get(current).cameFrom) {
      current = info.get(current).cameFrom!;

      yield current;
    }
  }
}
